import React from 'react';
import { Check, Clock, Copy, MessageSquare, UserPlus, X } from 'lucide-react';
import { Icon } from './Icon';
import Button from './ui/Button';
import { useToast } from '../toastContext';

export default function AccessRequestsPage({ axiosAuth, theme, isDarkMode, accessRequests, setAccessRequests }) {
  const { pushToast } = useToast();
  const [tab, setTab]           = React.useState('pending');  // 'pending' | 'history'
  const [history, setHistory]   = React.useState([]);
  const [histLoading, setHistLoading] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState(null);

  React.useEffect(() => {
    if (tab !== 'history') return;
    setHistLoading(true);
    axiosAuth.get('/admin/access-requests/history')
      .then(r => setHistory(r.data || []))
      .catch(() => {})
      .finally(() => setHistLoading(false));
  }, [tab]);

  const logoSvgRaw = `<svg xmlns="http://www.w3.org/2000/svg" width="220" height="83" viewBox="0 0 900 340"><defs><style>.w{fill:#1a1a2e}.o1{opacity:.2}.o2{opacity:.4}.o3{opacity:.6}.o4{opacity:.9}.t{font-family:Georgia,serif;font-size:66px}.s{fill:#8a8a90;font-family:Helvetica,Arial,sans-serif;font-size:30px;font-weight:700;letter-spacing:.06em}</style></defs><g><circle cx="451.2" cy="96.3" r="52" fill="none" stroke="#1a1a2e" stroke-width="3"/><rect x="420" y="92.15" width="5.5" height="8.3" rx="2.75" class="w o1"/><rect x="427.8" y="89.65" width="5.5" height="13.3" rx="2.75" class="w o2"/><rect x="435.6" y="84.55" width="5.5" height="23.5" rx="2.75" class="w o3"/><rect x="443.4" y="78.15" width="5.5" height="35.9" rx="2.75" class="w o4"/><rect x="451.2" y="75.45" width="5.5" height="41.7" rx="2.75" class="w"/><rect x="459" y="78.15" width="5.5" height="35.9" rx="2.75" class="w o4"/><rect x="466.8" y="84.55" width="5.5" height="23.5" rx="2.75" class="w o3"/><rect x="474.6" y="89.65" width="5.5" height="13.3" rx="2.75" class="w o2"/><rect x="482.4" y="92.15" width="5.5" height="8.3" rx="2.75" class="w o1"/><text class="t w" x="109.6" y="222.3">Giving Words to Voices</text><text class="s" x="168.6" y="281.3">ARCHIVE TRANSCRIPTION PLATFORM</text></g></svg>`;
  const logoB64 = `data:image/svg+xml;base64,${btoa(logoSvgRaw)}`;

  const emailChrome = (bodyContent) => `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"><title>Giving Words to Voices\u2122</title></head>
<body style="margin:0;padding:0;background:#f4f4f5;font-family:Georgia,'Times New Roman',serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f5;padding:40px 0;">
  <tr><td align="center">
    <table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.10);">
      <tr><td style="background:linear-gradient(135deg,#1e3a5f 0%,#0f2035 100%);padding:28px 36px;">
        <p style="margin:0;font-family:Georgia,serif;font-size:22px;font-weight:400;color:#f1f5f9;letter-spacing:0.3px;">Giving Words to&nbsp;Voices&#8482;</p>
        <p style="margin:6px 0 0;font-family:Arial,sans-serif;font-size:11px;color:#93c5fd;letter-spacing:0.08em;text-transform:uppercase;">Archival Testimony Project</p>
      </td></tr>
      <tr><td style="padding:36px 36px 28px;">
        <div style="font-family:Georgia,serif;font-size:15px;line-height:1.75;color:#1a1a2e;">${bodyContent}</div>
      </td></tr>
      <tr><td style="padding:0 36px 28px;">
        <p style="margin:0;font-family:Georgia,serif;font-size:15px;color:#1a1a2e;line-height:1.75;">Warm regards,<br><strong>Michael Schwarz</strong></p>
      </td></tr>
      <tr><td style="padding:0 36px;"><hr style="border:none;border-top:1px solid #e5e7eb;margin:0;"></td></tr>
      <tr><td style="padding:20px 36px 28px;">
        <img src="${logoB64}" width="220" height="83" alt="Giving Words to Voices™" style="display:block;">
        <p style="margin:8px 0 0;font-family:Arial,sans-serif;font-size:11px;color:#94a3b8;">
          <a href="https://app.givingwordstovoices.com" style="color:#94a3b8;text-decoration:none;">app.givingwordstovoices.com</a>
        </p>
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`;

  // Returns true only when the draft window actually opened. Callers must not
  // clear the request off the Pending list unless the reviewer got the draft —
  // dismiss() is a one-way POST, so a blocked popup would otherwise lose the
  // request permanently with no reply ever composed.
  const previewEmail = (html, title) => {
    const win = window.open('', '_blank', 'width=680,height=780,resizable=yes,scrollbars=yes');
    if (!win) return false;
    win.document.write(html);
    win.document.title = title;
    win.document.close();
    return true;
  };

  // Compose the reply first, then retire the request — never the other way round.
  const previewThenDismiss = (html, title, id, outcome) => {
    if (previewEmail(html, title)) {
      dismiss(id, outcome);
    } else {
      pushToast('access-request-popup-blocked', 'error', 'The email draft could not open — your browser blocked the popup. Allow popups for this site and try again. This request has been kept in Pending.');
    }
  };

  // Record WHICH decision was taken, not just that the request was handled.
  // Approved, declined and dismissed used to be indistinguishable in history,
  // so a reviewer coming back could not tell who they admitted from who they
  // turned away.
  const dismiss = (id, outcome = 'dismissed') => {
    axiosAuth.post(`/admin/access-requests/${id}/dismiss`, { outcome })
      .then(() => setAccessRequests(rs => rs.filter(r => r.id !== id)))
      .catch(() => pushToast('access-request-update-error', 'error', 'Could not update this request. Please try again.'));
  };

  const fallbackCopy = (text, done) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      done();
    } catch { /* clipboard unavailable */ }
  };

  const copyEmail = (email, id) => {
    const text = email || '';
    const flash = () => { setCopiedId(id); setTimeout(() => setCopiedId(c => (c === id ? null : c)), 1000); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(flash).catch(() => fallbackCopy(text, flash));
    } else {
      fallbackCopy(text, flash);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const approveHtml = (req) => emailChrome(`
    <p>Dear ${req.name || 'Applicant'},</p>
    <p>Thank you for your interest in joining the Giving Words to Voices\u2122 project. We are delighted to welcome you to our team.</p>
    <p>Please find below your access details:</p>
    <table cellpadding="0" cellspacing="0" style="margin:20px 0;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:16px 20px;">
      <tr>
        <td style="font-family:Arial,sans-serif;font-size:13px;color:#64748b;padding:5px 16px 5px 0;white-space:nowrap;">Website</td>
        <td style="font-family:Arial,sans-serif;font-size:13px;color:#1a1a2e;padding:5px 0;"><a href="https://app.givingwordstovoices.com" style="color:#3b82f6;">app.givingwordstovoices.com</a></td>
      </tr>
      <tr>
        <td style="font-family:Arial,sans-serif;font-size:13px;color:#64748b;padding:5px 16px 5px 0;white-space:nowrap;">Username</td>
        <td style="font-family:Arial,sans-serif;font-size:13px;color:#1a1a2e;padding:5px 0;">${req.email}</td>
      </tr>
      <tr>
        <td style="font-family:Arial,sans-serif;font-size:13px;color:#64748b;padding:5px 16px 5px 0;white-space:nowrap;">Password</td>
        <td style="font-family:Arial,sans-serif;font-size:13px;color:#ef4444;padding:5px 0;font-style:italic;">[set a temporary password here]</td>
      </tr>
    </table>
    <p>Once you have logged in, you will be able to view your assigned interviews and begin working through the transcription and translation pipeline.</p>
    <p>If you have any questions, please don't hesitate to reach out.</p>
  `);

  const declineHtml = (req) => emailChrome(`
    <p>Dear ${req.name || 'Applicant'},</p>
    <p>Thank you for showing your interest in the Giving Words to Voices\u2122 project, and for taking the time to reach out to us.</p>
    <p>Unfortunately, we are not currently onboarding new volunteers. We appreciate your enthusiasm for this important work, and we hope you will stay in touch.</p>
  `);

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px', background: theme.bg }}>
      {/* Page header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
          <Icon icon={UserPlus} size={20} color={theme.glossaryAccent} />
          <h1 style={{ margin: 0, fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '1.7rem', fontWeight: 400, color: theme.text, letterSpacing: '0.02em' }}>
            Access Requests
          </h1>
          {accessRequests.length > 0 && (
            <span style={{ padding: '2px 10px', borderRadius: '12px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: '0.72rem', fontWeight: 700 }}>
              {accessRequests.length} pending
            </span>
          )}
        </div>
        <p style={{ margin: 0, fontSize: '0.82rem', color: theme.textSub }}>
          Review and respond to volunteer access requests.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: `1px solid ${theme.border}`, paddingBottom: '0' }}>
        {[['pending', 'Pending', accessRequests.length], ['history', 'Reviewed', null]].map(([id, label, count]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding: '8px 18px', fontSize: '0.78rem', fontWeight: 700, border: 'none',
            borderBottom: tab === id ? `2px solid ${theme.glossaryAccent}` : '2px solid transparent',
            background: 'transparent', cursor: 'pointer', color: tab === id ? theme.text : theme.textSub,
            marginBottom: '-1px', transition: '0.15s', display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            {label}
            {count != null && count > 0 && (
              <span style={{ padding: '1px 7px', borderRadius: '10px', background: 'rgba(239,68,68,0.12)', color: '#ef4444', fontSize: '0.65rem', fontWeight: 800 }}>{count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── PENDING TAB ── */}
      {tab === 'pending' && (
        <>
          {accessRequests.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0', color: theme.textSub }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                <Icon icon={Check} size={24} color={theme.success} />
              </div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: theme.text, marginBottom: '6px' }}>All clear</div>
              <div style={{ fontSize: '0.82rem' }}>No pending access requests at this time.</div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '680px' }}>
            {accessRequests.map(req => (
              <div key={req.id} style={{ background: theme.sidebar, border: `1px solid ${theme.border}`, borderRadius: '14px', overflow: 'hidden' }}>
                <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>{(req.name || req.email || '?')[0].toUpperCase()}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div dir="auto" style={{ fontWeight: 700, fontSize: '1rem', color: theme.text, marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{req.name || '(no name given)'}</div>
                    <div dir="auto" style={{ fontSize: '0.8rem', color: '#3b82f6', fontWeight: 500, marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{req.email}</div>
                    <div style={{ fontSize: '0.68rem', color: theme.textSub, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon icon={Clock} size={10} />Requested {fmtDate(req.requested_at)}
                    </div>
                  </div>
                  <button onClick={() => copyEmail(req.email, req.id)} title={copiedId === req.id ? 'Copied' : 'Copy email'}
                    style={{ padding: '6px 8px', borderRadius: '8px', background: 'transparent', border: `1px solid ${theme.border}`, cursor: 'pointer', color: copiedId === req.id ? theme.success : theme.textSub, flexShrink: 0 }}>
                    <Icon icon={copiedId === req.id ? Check : Copy} size={14} />
                  </button>
                </div>
                {req.message && (
                  <div style={{ margin: '0 20px 4px', padding: '12px 14px', background: isDarkMode ? 'rgba(59,130,246,0.06)' : 'rgba(59,130,246,0.04)', border: `1px solid rgba(59,130,246,0.15)`, borderRadius: '10px' }}>
                    <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Icon icon={MessageSquare} size={9} color="#3b82f6" />Message
                    </div>
                    <div dir="auto" style={{ fontSize: '0.8rem', color: isDarkMode ? '#cbd5e1' : '#374151', lineHeight: 1.6, fontStyle: 'italic' }}>"{req.message}"</div>
                  </div>
                )}
                <div style={{ padding: '14px 20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button onClick={() => previewThenDismiss(approveHtml(req), 'Approval — ' + req.name, req.id, 'approved')}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px', borderRadius: '9px', background: 'rgba(34,197,94,0.1)', border: '1.5px solid rgba(34,197,94,0.3)', cursor: 'pointer', color: '#22c55e', fontWeight: 700, fontSize: '0.82rem' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(34,197,94,0.18)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(34,197,94,0.1)'}>
                    <Icon icon={Check} size={15} color="#22c55e" />Approve
                  </button>
                  <button onClick={() => previewThenDismiss(declineHtml(req), 'Decline — ' + req.name, req.id, 'declined')}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px', borderRadius: '9px', background: 'rgba(239,68,68,0.08)', border: '1.5px solid rgba(239,68,68,0.25)', cursor: 'pointer', color: '#ef4444', fontWeight: 700, fontSize: '0.82rem' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.15)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}>
                    <Icon icon={X} size={15} color="#ef4444" />Decline
                  </button>
                  <Button theme={theme} variant="outline" size="sm" onClick={() => dismiss(req.id, 'dismissed')} style={{ marginLeft: 'auto' }}>
                    Dismiss
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === 'history' && (
        <>
          {histLoading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: theme.textSub, fontSize: '0.82rem' }}>Loading history…</div>
          )}
          {!histLoading && history.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', color: theme.textSub }}>
              <div style={{ fontSize: '0.82rem' }}>No reviewed requests yet.</div>
            </div>
          )}
          {!histLoading && history.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '680px' }}>
              {history.map(req => (
                <div key={req.id} style={{ background: theme.sidebar, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '14px', opacity: 0.8 }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: isDarkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: theme.textSub, fontWeight: 700, fontSize: '0.95rem' }}>{(req.name || req.email || '?')[0].toUpperCase()}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: theme.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{req.name || '(no name)'}</div>
                    <div style={{ fontSize: '0.75rem', color: theme.textSub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{req.email}</div>
                    {req.message && (
                      <div style={{ fontSize: '0.72rem', color: theme.textSub, fontStyle: 'italic', marginTop: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{req.message}"</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.65rem', color: theme.textSub, marginBottom: '2px' }}>Requested</div>
                    <div style={{ fontSize: '0.72rem', color: theme.textSub, fontFamily: 'monospace' }}>{fmtDate(req.requested_at)}</div>
                    {(() => {
                      // Show the actual decision. Rows reviewed before outcomes
                      // were recorded have no outcome, so they stay a neutral
                      // "Reviewed" rather than being guessed as approved.
                      const oc = req.outcome;
                      const styles = {
                        approved:  { colour: theme.success,  icon: Check, label: 'Approved' },
                        declined:  { colour: theme.danger,   icon: X,     label: 'Declined' },
                        dismissed: { colour: theme.textSub,  icon: Clock, label: 'Dismissed' },
                      };
                      const s = styles[oc] || { colour: theme.textSub, icon: Check, label: 'Reviewed' };
                      return (
                        <div style={{ fontSize: '0.65rem', color: s.colour, marginTop: '4px', fontWeight: oc ? 700 : 400 }}>
                          <Icon icon={s.icon} size={9} color={s.colour} /> {s.label} {fmtDate(req.reviewed_at)}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

    </div>
  );
}
